<template>
  <div
    v-if="!loading"
    :class="user ? '' : 'ma-6 sn-body-1'"
  >
    <div
      v-if="user"
      class="user-actions"
    >
      <v-flex class="md12">
        <div>{{ user.firstName }} {{ user.lastName }} is {{ correctArticle }} {{ accountType }} user</div>
      </v-flex>
    </div>
    <div v-else-if="role && newRole">
      <div class="pt-6 mb-3">
        {{ role.showRoleDescription ? role.roleDescription : '' }}
      </div>
    </div>
    <sr-permission-groups
      :permission-groups="requestPermissionsToChange"
      :view-only="isViewOnly"
      :expanded="expanded"
      @permissionChange="handleChange($event)"
    />
    <v-col
      v-if="!user && !role && !apiClient"
      class="text-right"
    >
      <sn-btn
        v-if="settingsToChange.length > 0"
        type="secondary"
        text="Cancel"
        size="standard"
        @click="checkForUnsavedChanges"
      />
      <sn-btn
        type="primary"
        text="Save"
        size="standard"
        :disabled="settingsToChange.length < 1"
        :loading="savingData"
        @click="save"
      />
    </v-col>

    <sn-modal
      v-model="saveChangesModalVisible"
      type="decision"
      title="Save changes?"
      data-cy="confirmation-modal"
      primary-text="Yes"
      secondary-text="Cancel"
      @submit="save"
      @input="cancel"
    >
      <span>There are unsaved edits on this page. Tap save to keep those changes.</span>
    </sn-modal>
  </div>
</template>

<script>

import SrPermissionGroups from './srui/srPermissionGroups'
import { companyRole, updateDefaultPermissions } from './graphql/signing_room_queries.gql'
import { apiClientDefaultPermissions } from './graphql/api_client_queries.gql'
import { mapMutations, mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'
import snSigningRoomService from '../services/snSigningRoomService'

export default {
  name: 'Permissions',
  components: {
    SrPermissionGroups
  },
  props: {
    user: {
      type: Object,
      default: () => {},
      required: false
    },
    role: {
      type: Object,
      required: false,
      default: () => {}
    },
    newRole: {
      type: Boolean,
      required: false,
      default: false
    },
    companyGuid: {
      type: String,
      required: false,
      default: null
    },
    apiClient: {
      type: Object,
      required: false,
      default: () => {}
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    expanded: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      savingData: false,
      activeTab: null,
      pendingTabToSwitch: null,
      tabs: [],
      companyRoles: null,
      nonDefaultPermissions: [],
      settingsToChange: [],
      requestPermissionsToChange: [],
      saveChangesModalVisible: false,
      toRoute: null // for back navigation route guard
    }
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'user'
    }),

    accountType () {
      return this.user ? this.user.roleType : this.tabs.length > 0 ? this.tabs.find(tab => tab.key === this.activeTab).name : ''
    },

    correctArticle () {
      return /a|e|i|o|u/.test(this.accountType.toLowerCase().charAt(0)) ? 'an' : 'a'
    },

    managesUsers () {
      return snSigningRoomService.hasPermission(this.loggedInUser, 'manageUsers')
    },

    isViewOnly () {
      return this.viewOnly || (this.user && !this.managesUsers)
    }
  },
  watch: {
    user () {
      this.getRoles()
    }
  },
  created () {
    if (this.apiClient != null) {
      if (this.apiClient.permissions != null && this.apiClient.permissions.length > 0) {
        this.requestPermissionsToChange = this.apiClient.permissions
      } else {
        this.getApiClientPermissions()
      }
      this.loading = false
    } else {
      this.getRoles()
    }
  },
  methods: {
    ...mapMutations(['setHeaderText', 'setSuccessToast', 'setErrorToastType', 'resetAppTimeout']),

    getApiClientPermissions () {
      this.loading = true
      this.$apollo.query({
        query: apiClientDefaultPermissions,
        fetchPolicy: 'no-cache'
      }).then(({ data: { apiClientDefaultPermissions } }) => {
        if (apiClientDefaultPermissions) {
          this.requestPermissionsToChange = JSON.parse(apiClientDefaultPermissions)
        }
      }).catch(err => console.error('Get default api client permissions failed:', err))
      this.loading = false
    },

    getRoles () {
      this.loading = true
      this.$apollo.query({
        query: companyRole,
        fetchPolicy: 'no-cache',
        variables: {
          guid: this.role?.roleGuid,
          name: this.user?.roleType,
          companyGuid: this.companyGuid
        }
      }).then(({ data: { companyRole } }) => {
        if (companyRole) {
          this.companyRoles = [{
            guid: companyRole.guid,
            name: companyRole.name,
            key: companyRole.name.toLowerCase(),
            companyId: companyRole.companyId,
            permissions: JSON.parse(companyRole.permissions)
          }]
          if (this.user) {
            this.activeTab = this.user.roleType.toLowerCase()
            this.requestPermissionsToChange = JSON.parse(this.user.permissionsGrouped)
            this.nonDefaultPermissions = this.user.nonDefaultPermissions
          } else if (this.role) {
            this.requestPermissionsToChange = this.role.rolePermissions.length > 0 ? this.role.rolePermissions : JSON.parse(companyRole.newRoleDefaultPermissions)
          }
        }
      }).catch(err => console.error('Get company roles failed:', err))
      this.loading = false
    },
    setTabs () {
      // push role key and role name as tab object
      this.companyRoles.forEach(role => {
        this.tabs.push({
          key: role.key,
          name: role.name
        })
      })

      this.activeTab = this.tabs[0].key
    },

    changeActiveTab (name) {
      if (this.savingData) { // so users can't switch tabs during a save
        return
      }
      if (this.activeTab !== name) {
        this.checkForUnsavedChanges(true, name)
      }
    },

    getDefaultPermissions (value) {
      return cloneDeep(this.companyRoles.find(cr => cr.key === value).permissions)
    },

    handleChange (changedPermissions) {
      changedPermissions.forEach(permission => {
        if (!this.settingsToChange.includes(permission)) {
          this.settingsToChange.push(permission)
        } else {
          this.settingsToChange = this.settingsToChange.filter(changedSetting => changedSetting !== permission)
        }
        if (this.user) {
          if (!this.nonDefaultPermissions.includes((permission))) {
            this.nonDefaultPermissions.push(permission)
          } else {
            this.nonDefaultPermissions = this.nonDefaultPermissions.filter(changedSetting => changedSetting !== permission)
          }
          this.$emit('change', [this.settingsToChange, this.requestPermissionsToChange])
        }
        if (this.role || this.apiClient) {
          this.$emit('change', [this.settingsToChange, this.requestPermissionsToChange])
        }
      })
    },

    save () {
      if (this.user) {
        this.saveChangesModalVisible = false
        this.$emit('submit')
      } else {
        this.savingData = true
        const currentRole = this.companyRoles.find(role => role.key === this.activeTab)
        this.$apollo.mutate({
          mutation: updateDefaultPermissions,
          variables: {
            companyId: currentRole.companyId,
            roleGuid: currentRole.guid,
            permissions: JSON.stringify(this.requestPermissionsToChange)
          }
        }).then(({ data }) => {
          if (data.updateDefaultPermissions.response) {
            this.updateDefaultPermissions(currentRole.guid)
            this.setSuccessToast({ description: data.updateDefaultPermissions.response })
            this.settingsToChange = [] // disable save button
            if (this.user) {
              this.$emit('close')
            }
          } else if (data.updateDefaultPermissions.error) {
            this.setErrorToastType({ description: 'There was an error in saving permissions: ' + data.updateDefaultPermissions.error })
          }
          this.savingData = false
          this.saveChangesModalVisible = false
          if (this.toRoute) {
            this.$router.push(this.toRoute)
          }
        }).catch(err => {
          this.setErrorToastType({ description: 'There was an error in saving permissions: ' + err })
          this.savingData = false
          this.saveChangesModalVisible = false
        })
      }
    },

    checkForUnsavedChanges (isTabSwitch = false, switchedTab = null) {
      if (this.settingsToChange.length > 0) {
        this.saveChangesModalVisible = true
        this.pendingTabToSwitch = switchedTab
      }
      if (isTabSwitch && this.settingsToChange.length === 0) {
        this.activeTab = switchedTab
        this.resetState()
      }
    },

    cancel () {
      this.checkPendingTabSwitch() // if we cancelled from unsaved changes modal when switching tabs, we need to finish switching the active tab
      if (!this.user) { this.resetState() }
      this.saveChangesModalVisible = false
      this.toRoute = null
    },

    resetState () {
      this.requestPermissionsToChange = this.getDefaultPermissions(this.activeTab)
      this.settingsToChange = []
      if (this.user) { this.$emit('change', [this.settingsToChange, this.requestPermissionsToChange]) }
    },

    updateDefaultPermissions (roleGuid) {
      this.companyRoles.find(cr => cr.guid === roleGuid).permissions = this.requestPermissionsToChange
      this.checkPendingTabSwitch() // if we saved from unsaved changes modal when switching tabs, we need to finish switching the active tab
      this.requestPermissionsToChange = this.getDefaultPermissions(this.activeTab)
    },

    checkPendingTabSwitch () {
      if (this.pendingTabToSwitch) {
        this.activeTab = this.pendingTabToSwitch
        this.pendingTabToSwitch = null
      }
    }
  },

  beforeRouteLeave (to, from, next) {
    if (this.settingsToChange.length > 0) {
      this.toRoute = to
      this.saveChangesModalVisible = true
    } else {
      next()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.sn-btn {
  margin-left: 4px;
}

.user-actions {
  margin-top: 28px;
  margin-bottom: 28px;
}

.user-actions > * {
  padding: 5px 0 5px 0;

  button {
    margin: 0 !important;
    text-align: left !important;
  }
}

</style>
